<script setup>
/*
------------------------------------------------------------------------
Base Modal Dialog Component

A centered dialog modal for alerts, confirmations, and validation messages.
Provides consistent behavior for all dialog-style modals:
- Escape key to close
- Body scroll lock
- Backdrop click to close (optional)
- Fade transition animation
- Focus trap (Tab/Shift+Tab cycles within modal)

@slot default - The modal content
@prop {Function} onClose - Required callback when modal should close
@prop {boolean} showBackdrop - Whether to show backdrop (default: true)
@prop {string} maxWidth - Max width class (default: 'max-w-[600px]')
------------------------------------------------------------------------
*/
import { ref, onMounted, onUnmounted, nextTick } from "vue";
import { PhX } from "@phosphor-icons/vue";

const props = defineProps({
	onClose: {
		type: Function,
		required: true,
	},
	showBackdrop: {
		type: Boolean,
		default: true,
	},
	maxWidth: {
		type: String,
		default: "max-w-[600px]",
	},
	showCloseButton: {
		type: Boolean,
		default: true,
	},
});

const dialogRef = ref(null);
let previouslyFocusedElement = null;

const FOCUSABLE_SELECTORS = [
	"button:not([disabled])",
	"input:not([disabled])",
	"select:not([disabled])",
	"textarea:not([disabled])",
	"a[href]",
	'[tabindex]:not([tabindex="-1"])',
].join(", ");

const getFocusableElements = () => {
	if (!dialogRef.value) return [];
	return Array.from(dialogRef.value.querySelectorAll(FOCUSABLE_SELECTORS));
};

const handleKeydown = (event) => {
	if (event.key === "Escape") {
		props.onClose();
		return;
	}

	if (event.key === "Tab") {
		const focusableElements = getFocusableElements();
		if (focusableElements.length === 0) {
			event.preventDefault();
			return;
		}

		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];

		if (event.shiftKey) {
			if (document.activeElement === firstElement) {
				event.preventDefault();
				lastElement.focus();
			}
		} else {
			if (document.activeElement === lastElement) {
				event.preventDefault();
				firstElement.focus();
			}
		}
	}
};

onMounted(async () => {
	previouslyFocusedElement = document.activeElement;
	window.addEventListener("keydown", handleKeydown);
	// Set scrollbar width CSS variable to prevent page jump when hiding scrollbar
	const scrollbarWidth =
		window.innerWidth - document.documentElement.clientWidth;
	document.body.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);
	document.body.classList.add("no-scroll");

	await nextTick();
	const focusableElements = getFocusableElements();
	if (focusableElements.length > 0) {
		focusableElements[0].focus();
	} else if (dialogRef.value) {
		dialogRef.value.focus();
	}
});

onUnmounted(() => {
	window.removeEventListener("keydown", handleKeydown);
	document.body.classList.remove("no-scroll");
	document.body.style.removeProperty("--scrollbar-width");

	if (
		previouslyFocusedElement &&
		typeof previouslyFocusedElement.focus === "function"
	) {
		previouslyFocusedElement.focus();
	}
});
</script>

<template>
	<!-- Backdrop -->
	<div
		v-if="showBackdrop"
		class="fixed inset-0 z-[140] bg-black/80 transition-opacity"
		@click="onClose"
	></div>

	<!-- Dialog -->
	<div
		ref="dialogRef"
		tabindex="-1"
		role="dialog"
		aria-modal="true"
		class="lux-modal sm:px-18 fixed left-1/2 top-1/2 z-[150] mx-auto flex h-auto w-full max-w-[95%] -translate-x-1/2 -translate-y-1/2 transform flex-col bg-white px-5 py-8 text-center text-black shadow-lg focus:outline-none sm:py-16 sm:pb-8 lg:max-w-[400px]"
		:class="maxWidth"
	>
		<slot></slot>

		<!-- Close Button -->
		<button
			v-if="showCloseButton"
			type="button"
			class="spin-effect absolute right-[15px] top-[15px] h-[16px] w-[16px] focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
			@click="onClose"
		>
			<PhX
				:size="25"
				aria-hidden="true"
				class="lux-pos-absmiddle text-blacka75 hover:text-black"
			/>
		</button>
	</div>
</template>
